import { mm } from "@inovitas/infra3dsdk";
import {
  DeleteForeverOutlined,
  FileDownloadOutlined,
  MoreHoriz,
  SettingsOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { TreeItem } from "@mui/lab";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import { UseActiveLayer } from "shared/common/hooks/UseActiveLayer";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore, Topics } from "shared/common/stores/sidebarStore";
import { getIconForGeomType } from "shared/common/utils/layers/layerutils";
import LayerDownloadDialog from "./LayerDownloadDialog";
import {
  getLayersAddedByUser,
  saveLayersToLocalStorage,
} from "shared/common/utils/layers/localStorageLayers";

interface props {
  topic: Topics[];
  handleChange: (index: number[], showOnMap: boolean) => void;
  setChangeState: Dispatch<SetStateAction<boolean>>;
  changeState: boolean;
  changeExportFile: (id: string, visible: boolean) => void;
  checkShowFolder: (index: number) => void;
  children: Topics;
  index: number[];
  layerMenuID: string;
  setLayerMenuID: Dispatch<SetStateAction<string>>;
  length: number;
  handleCloseEverythingOnRemoveLayer: () => void;
}

export const LayertreeLayers = ({
  topic,
  handleChange,
  setChangeState,
  changeState,
  changeExportFile,
  checkShowFolder,
  children,
  index,
  layerMenuID,
  setLayerMenuID,
  length,
  handleCloseEverythingOnRemoveLayer,
}: props) => {
  const { t } = useTranslation();
  const apm = defaultStore((state) => state.apm);
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeEditLayerSettings = defaultStore(
    (state) => state.changeEditLayerSettings
  );
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const [dialogOpen, setDialogOpen] = useState(false);

  const editPopover = sidebarStore((state) => state.editPopover);
  const changeSelectedTableRows = sidebarStore(
    (state) => state.changeSelectedTableRows
  );

  const activeLayer = UseActiveLayer(viewer);

  //function to make layer visible or not visible
  const handleCallChanges = (showOnMap: boolean) => {
    if (viewer !== undefined) {
      handleChange(index, !showOnMap);

      if (children.layer) {
        if (showOnMap) {
          children.layer.setVisible(false);
          if (index.length > 1) checkShowFolder(index[0]);
          setChangeState(!changeState);
          changeExportFile(children.layer.id, false);
        } else {
          children.layer.setVisible(true);
          topic[index[0]].showOnMap = true;
          setChangeState(!changeState);
          changeExportFile(children.layer.id, true);
        }
      }
    }
  };

  //open layer settings window
  const handleLayerOptions = (item: Topics) => {
    setLayerMenuID(item.key ? item.key : "");
  };

  //close layer settings window
  const handleClose = () => {
    setLayerMenuID("");
  };

  //delete layer
  const deleteLayer = (layer: mm.Layer) => {
    const allLayersAddedFromUser = getLayersAddedByUser();
    allLayersAddedFromUser.forEach((item) => {
      item.layers = item.layers.filter((ls: any) => {
        if (ls.id !== layer.id) {
          return true;
        } else {
          return false;
        }
      });
    });
    saveLayersToLocalStorage(allLayersAddedFromUser);
    if (viewer) {
      viewer.getScene().getLayerHandler().removeLayer(layer);
      //if active layer has been deleted remove it from selection
      if (activeLayer === null) {
        handleCloseEverythingOnRemoveLayer();
      }
    }
    setLayerMenuID("");
  };

  //setting id of layer that we want to edit and open edit layer settings window
  const editLayer = (layer: mm.Layer) => {
    setLayerMenuID("");
    changeEditLayerSettings(layer);
  };

  const _handleLayerClick = () => {
    changeSelectedTableRows(undefined);
    viewer
      ?.getScene()
      .getLayerHandler()
      .getActiveLayer()
      ?.featureselector.clearSelection();
    if (children.layer) {
      viewer?.getScene().getLayerHandler().setActiveLayer(children.layer);
    }
  };

  return (
    <>
      <Box
        className="singleLayer manageLayerOptionsOnHover"
        position={"relative"}
        display={"flex"}
        flexDirection="row"
        alignItems={"center"}
        width="100%"
        marginBottom={index.length > 1 ? 0 : 0.5}
      >
        <Box
          position={"relative"}
          display={"flex"}
          flexDirection="row"
          alignItems={"center"}
          width="calc(100% - 24px)"
          sx={{
            color:
              activeLayer &&
              children.layer &&
              activeLayer.id == children.layer.id
                ? "secondary.main"
                : darkTheme
                  ? "white"
                  : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main", cursor: "pointer" },
            },
          }}
          onClick={() => {
            monitor(apm, "select-layer | Layertree", _handleLayerClick, {
              layername: children.name,
            });
          }}
        >
          {index.length > 1 && (
            <Box
              width={"20px"}
              height={index[index.length - 1] === length - 1 ? "16px" : "32px"}
              display={"flex"}
              alignItems={
                index[index.length - 1] === length - 1 ? "end" : "center"
              }
              marginLeft={"-12.5px"}
              marginTop={index[index.length - 1] === length - 1 ? "-16px" : ""}
              sx={{
                borderLeft: darkTheme
                  ? "1px white solid"
                  : "1px #05395277 solid",
              }}
            >
              <Box
                sx={{
                  width: "16px",
                  height: "0px",
                  borderTop: darkTheme
                    ? "1px white solid"
                    : "1px #05395277 solid",
                }}
              ></Box>
            </Box>
          )}
          <Box
            width={24}
            height={24}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            overflow={"hidden"}
          >
            {getIconForGeomType(children?.icon)}
          </Box>
          <TreeItem
            classes={{
              root: "infra3d-rootTreeItem",
              iconContainer: "infra3d-iconTreeItem",
              label:
                index.length > 1
                  ? "infra3d-labelTreeItem"
                  : "infra3d-firstLabelTreeItem",
              content: "infra3d-contentTreeItem",
              group: "infra3d-groupTreeItem",
            }}
            nodeId={children.key ? children.key : ""}
            label={children.title}
          />
        </Box>
        {children.addedFromUser && !editPopover && (
          <Box
            className={`showLayerOptionsOnHover ${
              layerMenuID === children.key
                ? "showLayerOptionsOnHoverImportant"
                : ""
            }`}
            sx={{
              "@media (hover: none)": {
                display:
                  activeLayer &&
                  children.layer &&
                  activeLayer.id == children.layer.id
                    ? "block"
                    : "none",
              },
            }}
          >
            <IconButton
              className="showOptionsButton"
              id={children.key}
              onClick={() =>
                monitor(
                  apm,
                  "show-layer-options | Layertree",
                  () => handleLayerOptions(children),
                  {
                    layername: children.name,
                  }
                )
              }
              disableRipple={true}
              sx={{
                padding: "0px",
                right: "24px",
                color: darkTheme ? "white" : "darkblue.main",
                "@media (hover: hover)": {
                  ":hover": { color: "secondary.main" },
                },
              }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={document.getElementById(
                children.key ? children.key : ""
              )}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={layerMenuID === children.key}
              onClose={handleClose}
              sx={{
                padding: 0,
                marginTop: "6px",
              }}
              classes={{
                list: darkTheme
                  ? "infra3d-listPopover"
                  : "infra3d-listPopoverWhite",
              }}
            >
              <MenuItem
                sx={{
                  gap: "10px",
                  alignItems: "center",
                  backgroundColor: darkTheme ? "darkblue.main" : "white",
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": {
                      color: "secondary.main",
                    },
                  },
                }}
                onClick={() =>
                  monitor(
                    apm,
                    "open-layer-settings | Layertree",
                    () => {
                      children.layer && editLayer(children.layer);
                    },
                    {
                      layername: children.name,
                    }
                  )
                }
              >
                <SettingsOutlined fontSize="small" />
                <Typography variant="body1" fontSize={"13px"} fontWeight={500}>
                  {t("edit_layers.edit_layer")}
                </Typography>
              </MenuItem>
              <MenuItem
                sx={{
                  gap: "10px",
                  alignItems: "center",
                  backgroundColor: darkTheme ? "darkblue.main" : "white",
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": {
                      color: "secondary.main",
                    },
                  },
                }}
                onClick={() => setDialogOpen(true)}
              >
                <FileDownloadOutlined fontSize="small" />
                <Typography variant="body1" fontSize={"13px"} fontWeight={500}>
                  {t("edit_layers.download_layer")}
                </Typography>
              </MenuItem>
              <MenuItem
                sx={{
                  gap: "10px",
                  alignItems: "center",
                  backgroundColor: darkTheme ? "darkblue.main" : "white",
                  color: darkTheme ? "white" : "darkblue.main",
                  "@media (hover: hover)": {
                    ":hover": {
                      color: "secondary.main",
                    },
                  },
                }}
                onClick={() =>
                  monitor(
                    apm,
                    "remove-layer | Layertree",
                    () => {
                      children.layer && deleteLayer(children.layer);
                    },
                    { layername: children.name }
                  )
                }
              >
                <DeleteForeverOutlined fontSize="small" />
                <Typography variant="body1" fontSize={"13px"} fontWeight={500}>
                  {t("edit_layers.remove_layer")}
                </Typography>
              </MenuItem>
            </Menu>
          </Box>
        )}
        <IconButton
          onClick={() =>
            monitor(
              apm,
              "change-visibility-layer | Layertree",
              () => handleCallChanges(children.showOnMap),
              { layername: children.name, visible: !children.showOnMap }
            )
          }
          disableRipple={true}
          sx={{
            padding: "0px",
            position: "absolute",
            right: "0px",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
        >
          {children.showOnMap && <VisibilityOutlined fontSize="small" />}
          {!children.showOnMap && <VisibilityOffOutlined fontSize="small" />}
        </IconButton>
      </Box>
      {children.layer && (
        <LayerDownloadDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          setLayerMenuID={setLayerMenuID}
          layer={children.layer}
        />
      )}
    </>
  );
};
